import Section from '@app/components/atoms/Section/Section'
import { VOUCHER_TYPE } from '@app/constant/VoucherTypeConstant'
import useRemember, { getStorageData, setStorageData } from '@app/libs/useRemember'
import { Inertia } from '@inertiajs/inertia'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import Empty from './Empty'
import Footer from './Footer'
import Header from './Header'
import ItemRender from './ItemRender'

const CustomPaper = styled(Paper)({
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  border: '1px solid rgb(224 224 224)',
  borderTopWidth: 0,
})

const CustomPaperHead = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  border: '1px solid rgb(224 224 224)',
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
}))

const CustomFooterSummary = styled(Paper)({
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  border: '1px solid rgb(224 224 224)',
  borderTopWidth: 1,
  width: '100%',
})

const CustomContainer = styled(Grid)(({ theme }) => ({
  background: theme.palette.grey[50],
  position: 'relative',
}))

const CustomFooter = styled(Grid)({
  position: 'sticky',
  bottom: 0,
  justifyContent: 'center',
})

const CustomSection = styled(Section)({
  paddingTop: '0px !important',
})

export default function CartPage() {
  const { t } = useTranslation()
  const [dataCart, setDataCart] = useState([])
  const [infoCart, setInfoCart] = useRemember('infoCart', {})

  const fetchCartVoucher = () => getStorageData('cartVoucher')
  const { data } = useQuery('listCartVoucher', fetchCartVoucher)

  const fetchCart = async () => {
    const data = await fetchCartVoucher()
    if (data && 0 < data.length) {
      setDataCart(data)
    }
  }

  const { handleSubmit, getValues, setValue, control, setError, clearErrors, register, watch } = useForm({
    defaultValues: {},
  })

  const onSubmit = () => {
    setInfoCart(getValues('data'))
    Inertia.get(route('partner.campaignCreate'), {
      onError: () => {},
    })
  }

  const commonProps = {
    clearErrors,
    getValues,
    setValue,
    control,
    setError,
    watch,
  }

  const queryClient = useQueryClient()
  const mutation = useMutation(fetchCart, {
    onSuccess: () => {
      queryClient.invalidateQueries('listCartVoucher')
    },
  })

  const handleRemoveVoucher = ({ id }) => {
    mutation.mutate()
    const newData = [...dataCart].map((item) => ({
      ...item,
      data: [...item.data.filter((voucher) => voucher.id !== id)],
    }))
    setDataCart(newData)
    setStorageData('cartVoucher', newData)
  }

  useEffect(() => {
    fetchCart()
  }, [])

  useEffect(() => {
    if (0 < dataCart.length && 0 < data.length) {
      const newForm = dataCart
        .map((item) => {
          if (VOUCHER_TYPE.PREMIUM === item.type) {
            return {
              [item.type]: item.data
                .map((item) => ({
                  id: item.id,
                  points: 0,
                  quantityCodes: item.limit_min < item.available_codes ? item.limit_min : item.available_codes,
                  estimateCost:
                    item.limit_min < item.available_codes
                      ? item.limit_min * item.price
                      : item.available_codes * item.price,
                }))
                .reduce(
                  (obj, item) => ({
                    ...obj,
                    [item['id']]: item,
                  }),
                  {}
                ),
            }
          }

          return {
            [item.type]: item.data
              .map((item) => ({
                id: item.id,
                points: 0,
                quantityCodes: 0,
                estimateCost: 0,
              }))
              .reduce(
                (obj, item) => ({
                  ...obj,
                  [item['id']]: item,
                }),
                {}
              ),
          }
        })
        .reduce(
          (obj, item) => ({
            ...obj,
            [Object.keys(item)[0]]: item[Object.keys(item)[0]],
          }),
          {}
        )

      if (0 === Object.keys(infoCart).length) {
        setValue('data', newForm)
        setInfoCart(newForm)
      } else {
        for (const key in newForm) {
          if (void 0 === infoCart[key] && 0 < Object.keys(newForm[key]).length) {
            infoCart[key] = { ...newForm[key] }
            for (const voucher in newForm[key]) {
              newForm[key][voucher] =
                infoCart[key][voucher] === undefined ? newForm[key][voucher] : infoCart[key][voucher]
            }
          }
        }
        setValue('data', newForm)
      }
    }
  }, [data, dataCart])

  return (
    <>
      {data && 0 < dataCart.reduce((total, item) => total + item.data.length, 0) ? (
        <Grid container component="form" onSubmit={handleSubmit(onSubmit)}>
          <CustomContainer item xs={12}>
            <Grid container justifyContent="center">
              <Grid item sx={{ paddingBottom: '40px' }} xs={12}>
                <List>
                  {(dataCart && 0 < dataCart.length ? dataCart : [])
                    ?.sort((a, b) => (a.type > b.type ? 1 : b.type > a.type ? -1 : 0))
                    .map((row, index) => (
                      <List key={`type-${index}`}>
                        {0 < row.data.length && (
                          <CustomSection>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Typography variant="h5">
                                  {' '}
                                  {VOUCHER_TYPE.PREMIUM === row?.type && row.type
                                    ? t('VOUCHER.PREMIUM')
                                    : t('VOUCHER.STANDARD')}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <CustomPaperHead>
                                  <Header type={row?.type} />
                                </CustomPaperHead>
                                {(row.data || []).map((item, itemIndex) => (
                                  <CustomPaper key={`item-${itemIndex}`}>
                                    {getValues('data') && (
                                      <ItemRender
                                        {...commonProps}
                                        handleRemoveVoucher={handleRemoveVoucher}
                                        item={item}
                                        register={register}
                                        setInfoCart={setInfoCart}
                                      />
                                    )}
                                  </CustomPaper>
                                ))}
                              </Grid>
                            </Grid>
                          </CustomSection>
                        )}
                      </List>
                    ))}
                </List>
              </Grid>
              <CustomFooter container>
                <CustomFooterSummary>
                  <Grid item xs={12}>
                    {getValues('data') && <Footer {...commonProps} />}
                  </Grid>
                </CustomFooterSummary>
              </CustomFooter>
            </Grid>
          </CustomContainer>
        </Grid>
      ) : (
        <Empty />
      )}
    </>
  )
}
